import { Link } from 'react-router'
import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react'
import { Logo } from '@/shared/components/ui/logo'
import { buttonVariants } from '@/shared/components/ui/button'
import { useConsumerBranding } from '@/consumer/hooks/use-consumer-branding'
import { LegalDocumentView } from '@/consumer/components/legal-document-view'
import { PRIVACY_POLICY, TERMS_AND_CONDITIONS, type LegalDoc } from '@/consumer/lib/legal-content'

type LegalKind = 'terms' | 'privacy'

const COPY: Record<
  LegalKind,
  {
    title: string
    urlKey: 'terms_url' | 'privacy_url'
    contentKey: 'terms_content' | 'privacy_content'
    /** Platform-default document rendered when the tenant sets no override. */
    fallback: LegalDoc
  }
> = {
  terms: {
    title: 'Terms & Conditions',
    urlKey: 'terms_url',
    contentKey: 'terms_content',
    fallback: TERMS_AND_CONDITIONS,
  },
  privacy: {
    title: 'Privacy Policy',
    urlKey: 'privacy_url',
    contentKey: 'privacy_content',
    fallback: PRIVACY_POLICY,
  },
}

/**
 * Public legal page (Terms & Conditions / Privacy Policy). Content resolution,
 * in precedence order: a tenant-configured hosted URL, then tenant-configured
 * inline content, then the platform-default document bundled with the app.
 * Renders without authentication so it can be opened from the onboarding step
 * before the account exists.
 */
export default function LegalPage({ kind }: { kind: LegalKind }) {
  const { data: branding, isLoading } = useConsumerBranding()
  const { title, urlKey, contentKey, fallback } = COPY[kind]
  const url = branding?.legal?.[urlKey]?.trim() ?? ''
  const content = branding?.legal?.[contentKey]?.trim() ?? ''

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="sticky top-0 z-10 h-14 border-b-[0.5px] border-b-[var(--rule)] bg-[var(--bg-card)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
          <Link to="/" className="cursor-pointer">
            <Logo size="sm" />
          </Link>
          <Link
            to="/"
            className={buttonVariants({
              variant: 'ghost',
              size: 'sm',
              className: 'gap-1.5 text-[var(--ink-3)]',
            })}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="mb-6 font-display text-[32px] text-[var(--ink)]">{title}</h1>

        {isLoading ? (
          <div className="flex items-center gap-2 text-[var(--ink-3)]">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : url ? (
          <div className="flex flex-col items-start gap-4">
            <p className="text-sm text-[var(--ink-2)]">
              Our {title} are hosted externally. Open them to read the full document.
            </p>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className={buttonVariants({ className: 'gap-1.5' })}
            >
              Open {title}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        ) : content ? (
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--ink-2)]">
            {content}
          </div>
        ) : (
          <LegalDocumentView doc={fallback} />
        )}
      </main>
    </div>
  )
}
